import { ChangeDetectionStrategy, Component, effect, inject, output, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { IconButtonComponent } from '@connect-services/client/components/icon-button/icon-button.component';
import {
    FieldInterventionUpdateScheduledEndDateForm,
    UpdateDateFieldInterventionModalComponent,
} from '@connect-services/client/components/modals/update-date-field-intervention-modal/update-date-field-intervention-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import {
    FieldIntervention,
    FieldInterventionsService,
    UpdateFieldInterventionDto,
} from '@connect-services/src/sdk/api';
import { materialModules, ressourcesModules } from '@ressources/client/ressources.module';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconButtonComponent, materialModules, ressourcesModules, TitleComponent],
    selector: 'app-field-intervention-title',
    styleUrl: './field-intervention-title.component.scss',
    templateUrl: './field-intervention-title.component.html',
})
export class FieldInterventionTitleComponent {
    public deleteFieldIntervention = output<void>();
    public editContextLayers = output<void>();
    public editFieldIntervention = output<void>();
    public editFieldInterventionArea = output<void>();

    protected readonly ActionsEnum = ActionsEnum;
    protected readonly fieldIntervention: WritableSignal<FieldIntervention | null> = signal(null);
    protected readonly FieldIntervention = FieldIntervention;
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);

    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(private matDialog: MatDialog) {
        effect(() => {
            if (!this.fieldInterventionBloc.value()) {
                return;
            }

            this.fieldIntervention.set(this.fieldInterventionBloc.value());
        });
    }

    public async goToPreviousPage(): Promise<void> {
        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id, RoutePathEnum.FIELD_INTERVENTIONS];
        await this.router.navigate(path);
    }

    public openFieldInterventionConfirmClosingModal(): void {
        if (!this.fieldIntervention()) {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('fieldInterventionClose'),
                description: this.translocoService.translate('irreversibleAction'),
                title: this.translocoService.translate('confirmCloseIntervention', {
                    entity: 'fieldInterventionEntity',
                }),
                type: ModalType.DANGER,
            },
            width: '30vw',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            const fieldInterventionId = this.fieldInterventionBloc.value()!.id;

            await firstValueFrom(
                this.fieldInterventionsService.fieldInterventionControllerPatch({
                    id: fieldInterventionId,
                    updateFieldInterventionDto: {
                        endDate: new Date().toISOString(),
                        status: UpdateFieldInterventionDto.StatusEnum.DONE,
                    },
                }),
            );

            await this.fieldInterventionBloc.load({ id: fieldInterventionId }, { refresh: true });
        });
    }

    public openFieldInterventionExtendEndDateModal(): void {
        if (!this.fieldIntervention()) {
            return;
        }

        const dialogRef = this.matDialog.open(UpdateDateFieldInterventionModalComponent, {
            autoFocus: false,
            data: {
                name: this.fieldIntervention()!.name,
                scheduledEndDate: this.fieldIntervention()?.scheduledEndDate ?? null,
                title: 'fieldInterventionExtend',
            },
            width: '40vw',
        });

        dialogRef
            .afterClosed()
            .subscribe(async (response: FieldInterventionUpdateScheduledEndDateForm | null): Promise<void> => {
                if (!response) {
                    return;
                }

                await this.updateFieldIntervention(response);
            });
    }

    private async updateFieldIntervention(data: FieldInterventionUpdateScheduledEndDateForm): Promise<void> {
        try {
            await firstValueFrom(
                this.fieldInterventionsService.fieldInterventionControllerPatch({
                    id: this.fieldInterventionBloc.value()!.id,
                    updateFieldInterventionDto: {
                        scheduledEndDate: data.controls.scheduledEndDate.value
                            ? new Date(data.controls.scheduledEndDate.value).toISOString()
                            : null,
                    },
                }),
            );

            await this.fieldInterventionBloc.load(
                {
                    id: this.fieldInterventionBloc.value()!.id,
                },
                { refresh: true },
            );

            this.snackBarService.openSuccessSnackBar('fieldInterventionEditionSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
        }
    }
}
